import React, { useEffect, useRef, useState } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormInput,
  CButton,
  CSpinner,
} from '@coreui/react'
import { CChartBar, CChartPie, CChartLine } from '@coreui/react-chartjs'
import { useDispatch, useSelector } from 'react-redux'
import { getSalesReports } from 'src/redux/salesreports'
import { generateColors } from 'src/utils/function'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const SalesReports = () => {
  const reportRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const dispatch = useDispatch()
  const { salesReportsData, loadingSalesReportsData, headersData, rowsData } = useSelector(
    (state) => state.salesreports,
  )

  const items = rowsData.map((item) => item.product_name)
  const revenue = rowsData.map((item) => item.total_revenue)
  const income = rowsData.map((item) => item.total_income)
  const sold = rowsData.map((item) => item.total_sold)
  const profit = rowsData.map((item) => item.profit)
  const coupons = rowsData.map((item) => item.coupons_discount)
  const cost = rowsData.map((item) => item.cost_per_item)

  useEffect(() => {
    const today = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(today.getDate() - 30)

    const formattedToday = today.toISOString().split('T')[0]
    const formattedThirtyDaysAgo = thirtyDaysAgo.toISOString().split('T')[0]

    setStartDate(formattedThirtyDaysAgo)
    setEndDate(formattedToday)
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(getSalesReports(startDate, endDate))
    }
  }, [dispatch, startDate, endDate])

  const exportToPDF = () => {
    setLoading(false)
    setTimeout(() => {
      const input = reportRef.current
      html2canvas(input, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF('p', 'mm', 'a4')
        const imgWidth = 210
        const pageHeight = 297
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        let heightLeft = imgHeight
        let position = 0

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        while (heightLeft > 0) {
          position = heightLeft - imgHeight
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }

        pdf.save(`Sales_Report_${startDate}_${endDate}.pdf`)
        setLoading(true)
      })
    }, 1000)
  }

  if (loadingSalesReportsData) return <CSpinner color="primary" className="d-block mx-auto my-4" />

  return (
    <CCard ref={reportRef} className="mb-1">
      <CCardHeader className="text-center text-white bg-dark">
        <CRow className="flex-row">
          <CCol>
            {loading && rowsData.length ? (
              <CButton color="primary" size="sm" onClick={exportToPDF}>
                Export as PDF
              </CButton>
            ) : null}
          </CCol>
          <CCol>
            <h4>RETAIL SALES REPORT</h4>
          </CCol>
          <CCol></CCol>
        </CRow>
        <CRow className="mt-2">
          <CCol>
            <strong>STORE NAME</strong>
            <br />
            K.G.N. Freshymeat
          </CCol>
          <CCol>
            <strong>SALES MANAGER</strong>
            <br />
            Sarfaraz Khan
          </CCol>
          <CCol>
            <CRow>
              <CCol md="2"></CCol>
              <CCol md="5">
                <strong>STARTING DATE</strong>
                <CFormInput
                  type="date"
                  name="startDate"
                  placeholder="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-1 py-0 mt-1"
                />
              </CCol>
              <CCol md="5">
                <strong>ENDING DATE</strong>
                <CFormInput
                  type="date"
                  name="endDate"
                  placeholder="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-1 py-0 mt-1"
                />
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </CCardHeader>

      <CCol className="m-1">
        <CCardBody>
          <CTable striped hover responsive bordered>
            <CTableHead>
              <CTableRow>
                {headersData?.map((header, index) => (
                  <CTableHeaderCell key={index}>{header}</CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rowsData?.map((row, rowIndex) => (
                <CTableRow key={rowIndex}>
                  {headersData?.map((header, cellIndex) => (
                    <CTableDataCell key={cellIndex}>
                      {row[header.toLowerCase().replace(/\s+/g, '_')]}
                    </CTableDataCell>
                  ))}
                </CTableRow>
              ))}
              <CTableRow>
                {headersData?.map((header, index) => {
                  if (index === 0) {
                    return (
                      <CTableDataCell key={index}>
                        <strong>Total</strong>
                      </CTableDataCell>
                    )
                  }
                  const total = rowsData?.reduce(
                    (sum, row) =>
                      sum + parseFloat(row[header.toLowerCase().replace(/\s+/g, '_')] || 0),
                    0,
                  )
                  return (
                    <CTableDataCell key={index}>
                      <strong>{total}</strong>
                    </CTableDataCell>
                  )
                })}
              </CTableRow>
            </CTableBody>
          </CTable>
        </CCardBody>
      </CCol>

      <CRow className="m-1">
        <CCol xs={6}>
          <CCard>
            <CCardHeader>Revenue Breakdown</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: items,
                  datasets: [
                    {
                      label: 'Revenue',
                      backgroundColor: '#f87979',
                      data: revenue,
                    },
                  ],
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6}>
          <CCard>
            <CCardHeader>Total Income Per Item</CCardHeader>
            <CCardBody>
              <CChartLine
                data={{
                  labels: items,
                  datasets: [
                    {
                      label: 'Total Income',
                      backgroundColor: '#FF6384',
                      borderColor: '#FF6384',
                      data: income,
                    },
                  ],
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="m-1">
        <CCol xs={4}>
          <CCard>
            <CCardHeader>Total Sold Per Item (Pie Chart)</CCardHeader>
            <CCardBody>
              <CChartPie
                data={{
                  labels: items,
                  datasets: [
                    {
                      data: sold,
                      backgroundColor: generateColors(items.length),
                    },
                  ],
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={8}>
          <CCard>
            <CCardHeader>Profit As Per Product</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: items,
                  datasets: [
                    {
                      label: 'Total Profit',
                      backgroundColor: generateColors(items.length),
                      data: profit,
                    },
                  ],
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCol className="m-3">
        <CTable striped hover responsive>
          <CTableHead color="dark">
            <CTableRow>
              <CTableHeaderCell className="text-center">#</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Total Orders</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Total Items Sold</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Total Revenue (₹)</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Delivery Charges (₹)</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Discount (₹)</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Total Earnings (₹)</CTableHeaderCell>
              <CTableHeaderCell className="text-center">Order Date</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {salesReportsData.map((order, index) => (
              <CTableRow key={index}>
                <CTableHeaderCell className="text-center">{index + 1}</CTableHeaderCell>
                <CTableDataCell className="text-center">{order.total_orders}</CTableDataCell>
                <CTableDataCell className="text-center">{order.total_items_sold}</CTableDataCell>
                <CTableDataCell className="text-center">₹{order.total_revenue}</CTableDataCell>
                <CTableDataCell className="text-center">
                  ₹{order.total_delivery_charges}
                </CTableDataCell>
                <CTableDataCell className="text-center">₹{order.total_discount}</CTableDataCell>
                <CTableDataCell className="text-center">₹{order.total_earnings}</CTableDataCell>
                <CTableDataCell className="text-center">
                  {new Date(order.order_date).toLocaleDateString()}
                </CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>
      </CCol>

      <CRow className="m-1 mt-4">
        <CCol md="4">
          <h6>PRODUCT COST PER ITEM</h6>
          <CChartPie
            data={{
              labels: items,
              datasets: [
                {
                  data: cost,
                  backgroundColor: generateColors(items.length),
                },
              ],
            }}
          />
        </CCol>
        <CCol md="8">
          <h6>COUPONS DISCOUNT</h6>
          <CChartLine
            data={{
              labels: items,
              datasets: [
                {
                  label: 'Coupons Discount',
                  backgroundColor: '#4BC0C0',
                  borderColor: '#4BC0C0',
                  data: coupons,
                  fill: false,
                },
              ],
            }}
          />
        </CCol>
      </CRow>
    </CCard>
  )
}

export default SalesReports
